import React, { useState, useEffect, useCallback } from 'react';
import { 
  View, 
  StyleSheet, 
  SafeAreaView, 
  ScrollView, 
  Alert, 
  ActivityIndicator,
  TouchableOpacity,
  Modal
} from 'react-native';
import { 
  Card, 
  Title, 
  Text, 
  Button, 
  useTheme,
  FAB,
  Badge,
  Portal,
  Dialog
} from 'react-native-paper';
import { useFocusEffect } from '@react-navigation/native';
import MaterialCommunityIcons from 'react-native-vector-icons/MaterialCommunityIcons';
import { getMyChildren } from '../../api/childrenAPI';
import { getMyActiveActivities, getMyPendingActivities, createActivity } from '../../api/activitiesAPI';

const HomeScreen = ({ navigation }) => {
  const theme = useTheme();
  const [children, setChildren] = useState([]);
  const [loadingChildren, setLoadingChildren] = useState(true);
  const [childrenError, setChildrenError] = useState(null);
  const [activeActivitiesCount, setActiveActivitiesCount] = useState(0);
  const [pendingActivitiesCount, setPendingActivitiesCount] = useState(0);
  const [loadingActiveCount, setLoadingActiveCount] = useState(false);
  const [loadingPendingCount, setLoadingPendingCount] = useState(false);
  const [isCreatingActivity, setIsCreatingActivity] = useState(false);
  const [warningModalVisible, setWarningModalVisible] = useState(false);
  const [warningMessage, setWarningMessage] = useState('');
  const [selectedChildName, setSelectedChildName] = useState('');
  const [confirmModalVisible, setConfirmModalVisible] = useState(false);
  const [selectedChildId, setSelectedChildId] = useState(null);

  // Yaş hesaplama fonksiyonu
  const formatAge = (birthdate) => {
    if (!birthdate) return '1 yaşından küçük';
    
    try {
      const birthDate = new Date(birthdate);
      const currentDate = new Date();
      
      // Yaş hesaplama
      let years = currentDate.getFullYear() - birthDate.getFullYear();
      let months = currentDate.getMonth() - birthDate.getMonth();
      let days = currentDate.getDate() - birthDate.getDate();
      
      // Ay düzeltme
      if (days < 0) {
        months--;
        // Önceki ayın gün sayısını hesapla
        const lastMonth = new Date(currentDate.getFullYear(), currentDate.getMonth(), 0);
        days += lastMonth.getDate();
      }
      
      // Yıl düzeltme
      if (months < 0) {
        years--;
        months += 12;
      }
      
      if (years >= 1) {
        return `${years} yaşında`;
      } else if (months >= 1) {
        return `${months} aylık`;
      } else {
        return `${days} günlük`;
      }
    } catch (error) {
      console.error('Yaş formatı hatası:', error);
      return '1 yaşından küçük';
    }
  };

  // Çocukları API'den getir
  const fetchChildren = async () => {
    try {
      setChildrenError(null);
      setLoadingChildren(true);
      const response = await getMyChildren();
      
      if (response.data) {
        // En fazla 3 çocuk gösterilsin
        const childrenData = response.data.slice(0, 2).map(child => ({
          id: child.id,
          name: child.first_name,
          surname: child.last_name,
          fullName: `${child.first_name} ${child.last_name}`,
          ageDisplay: formatAge(child.birthdate),
          dateOfBirth: child.birthdate,
          gender: child.gender
        }));
        
        setChildren(childrenData);
      }
    } catch (err) {
      console.error('Ana sayfada çocukları getirirken hata:', err);
      setChildrenError('Çocuk bilgileri yüklenemedi');
    } finally {
      setLoadingChildren(false);
    }
  };

  // Aktif aktivite sayısını getir
  const fetchActiveActivitiesCount = async () => {
    try {
      setLoadingActiveCount(true);
      const response = await getMyActiveActivities();
      
      if (response.success && response.data) {
        setActiveActivitiesCount(response.data.length);
      } else {
        setActiveActivitiesCount(0);
      }
    } catch (err) {
      console.error('Aktif aktiviteleri getirirken hata:', err);
      setActiveActivitiesCount(0);
    } finally {
      setLoadingActiveCount(false);
    }
  };
  
  // Onay bekleyen aktivite sayısını getir
  const fetchPendingActivitiesCount = async () => {
    try {
      setLoadingPendingCount(true);
      const response = await getMyPendingActivities();
      
      if (response.success && response.data) {
        setPendingActivitiesCount(response.data.length);
      } else {
        setPendingActivitiesCount(0);
      }
    } catch (err) {
      console.error('Onay bekleyen aktiviteleri getirirken hata:', err);
      setPendingActivitiesCount(0);
    } finally {
      setLoadingPendingCount(false);
    }
  };

  // Sayfa odaklandığında verileri yükle
  useFocusEffect(
    useCallback(() => {
      fetchChildren();
      fetchActiveActivitiesCount();
      fetchPendingActivitiesCount();
      
      return () => {};
    }, [])
  );

  // Oyun Parkı Girişi butonuna tıklandığında
  const handleParkEntry = () => {
    navigation.navigate('ParkEntry');
  };

  // Çocuk kart butonuna tıklandığında
  const handleChildParkEntry = (childId, childName) => {
    setSelectedChildId(childId);
    setSelectedChildName(childName);
    setConfirmModalVisible(true);
  };

  // Çocuk için doğrudan oyun parkı girişi
  const createParkEntryForChild = async () => {
    if (!selectedChildId) return;
    
    try {
      setIsCreatingActivity(true);
      setConfirmModalVisible(false);
      
      const entryData = {
        childId: selectedChildId,
        activityType: 'oyun-parki',
        startTime: new Date().toISOString(),
        notes: 'Oyun Parkı Girişi'
      };
      
      const response = await createActivity(entryData);
      
      if (response.success) {
        // Başarılı olduğunda aktivite sayfasına yönlendir
        navigation.navigate('Activities', { 
          screen: 'PendingActivities'
        });
        
        // Aktivite sayılarını yenile
        fetchPendingActivitiesCount();
      } else {
        console.error('Giriş kaydedilemedi:', response.message);
        
        // Hata mesajında "aktif veya onay bekleyen" içeriyor mu kontrol et
        if (response.message && response.message.includes("aktif veya onay bekleyen")) {
          setWarningMessage(response.message);
          setWarningModalVisible(true);
        } else {
          // Diğer hatalar için Alert kullan
          Alert.alert(
            'Hata',
            'Giriş kaydedilemedi: ' + (response.message || 'Bilinmeyen hata'),
            [{ text: 'Tamam' }]
          );
        }
      }
    } catch (error) {
      console.error('Giriş oluşturma hatası:', error);
      
      // Hata mesajında "aktif veya onay bekleyen" içeriyor mu kontrol et
      if (error.message && error.message.includes("aktif veya onay bekleyen")) {
        setWarningMessage(error.message);
        setWarningModalVisible(true);
      } else {
        // Diğer hatalar için Alert kullan
        Alert.alert(
          'Hata',
          'Giriş kaydedilemedi: ' + (error.message || 'Bilinmeyen hata'),
          [{ text: 'Tamam' }]
        );
      }
    } finally {
      setIsCreatingActivity(false);
    }
  };

  return (
    <SafeAreaView style={styles.container}>
      <ScrollView contentContainerStyle={styles.scrollContainer}>
        {/* Aktivite Kartları */}
        <View style={styles.activitiesContainer}>
          {/* Aktif Aktiviteler Kartı */}
          <Card style={styles.activityCard} onPress={() => navigation.navigate('Activities', { screen: 'ActiveParkActivities' })}>
            <Card.Content style={styles.activityCardContent}>
              <View style={[styles.activityIconContainer, { backgroundColor: 'rgb(125, 76, 236)' }]}>
                <MaterialCommunityIcons
                  name="gamepad-variant"
                  size={24}
                  color="white"
                />
              </View>
              <View style={styles.activityTextContainer}>
                <Title style={styles.activityTitle}>Aktif Ziyaretler</Title>
                {loadingActiveCount ? (
                  <ActivityIndicator size="small" color="rgb(125, 76, 236)" />
                ) : (
                  <Text style={{ fontSize: 14, color: '#666' }}>
                    {activeActivitiesCount} aktif ziyaret
                  </Text>
                )}
              </View>
              <MaterialCommunityIcons
                name="chevron-right"
                size={24}
                color="#757575"
              />
            </Card.Content>
          </Card>
          
          {/* Onay Bekleyen Aktiviteler Kartı */}
          <Card style={[styles.activityCard, {marginTop: 10}]} onPress={() => navigation.navigate('Activities', { screen: 'PendingActivities' })}>
            <Card.Content style={styles.activityCardContent}>
              <View style={[styles.activityIconContainer, { backgroundColor: 'rgb(125, 76, 236)' }]}>
                <MaterialCommunityIcons
                  name="clock-time-four"
                  size={24}
                  color="white"
                />
              </View>
              <View style={{ flex: 1, marginLeft: 10 }}>
                <Title style={styles.activityTitle}>Onay Bekleyenler</Title>
                {loadingPendingCount ? (
                  <ActivityIndicator size="small" color="rgb(125, 76, 236)" />
                ) : (
                  <Text style={{ fontSize: 14, color: '#666' }}>
                    {pendingActivitiesCount} onay bekleyen ziyaret
                  </Text>
                )}
              </View>
              <MaterialCommunityIcons
                name="chevron-right"
                size={24}
                color="#757575"
              />
            </Card.Content>
          </Card>
        </View>

        {/* Çocuklar Kartları */}
        <View style={styles.sectionHeader}>
          <Title style={styles.sectionTitle}>Çocuklarım</Title>
          <Button 
            mode="text" 
            onPress={() => navigation.navigate('Children')}
            labelStyle={{ fontSize: 14, fontWeight: 'bold', color: 'rgb(125, 76, 236)' }}
          >
            Tümünü Gör
          </Button>
        </View>

        {childrenError ? (
          <Card style={styles.errorCard}>
            <Card.Content>
              <Text style={styles.errorText}>{childrenError}</Text>
              <Button 
                onPress={fetchChildren} 
                style={{ marginTop: 10 }}
                textColor="rgb(125, 76, 236)"
                buttonColor="rgba(125, 76, 236, 0.1)"
              >
                Yeniden Dene
              </Button>
            </Card.Content>
          </Card>
        ) : loadingChildren ? (
          <View style={styles.loadingContainer}>
            <ActivityIndicator size="large" color="rgb(125, 76, 236)" />
            <Text style={styles.loadingText}>Çocuklar yükleniyor...</Text>
          </View>
        ) : children.length === 0 ? (
          <Card style={styles.emptyCard}>
            <Card.Content style={styles.emptyContent}>
              <MaterialCommunityIcons name="account-multiple" size={24} color="rgb(125, 76, 236)" />
              <Text style={styles.emptyText}>Henüz çocuk kaydınız bulunmamaktadır.</Text>
              <Button 
                onPress={() => navigation.navigate('AddChild')}
                mode="text"
                style={{ marginTop: 10 }}
                textColor="rgb(125, 76, 236)"
              >
                Çocuk Ekle
              </Button>
            </Card.Content>
          </Card>
        ) : (
          <View style={styles.childrenContainer}>
            {children.map((child) => (
              <Card key={child.id} style={styles.childCard}>
                <Card.Content style={styles.childCardContent}>
                  <View style={styles.childInfo}>
                    <View style={[styles.childAvatar, { backgroundColor: 'rgb(125, 76, 236)' }]}>
                      <MaterialCommunityIcons
                        name={child.gender === 'male' ? "face-man" : "face-woman"}
                        size={30}
                        color="white"
                      />
                    </View>
                    <View style={styles.childDetails}>
                      <Text style={styles.childName}>{child.fullName}</Text>
                      <Text style={styles.childAge}>{child.ageDisplay}</Text>
                    </View>
                  </View>
                  {/*
                  <Button 
                    mode="contained"
                    compact
                    style={[{ marginLeft: 'auto', paddingHorizontal: 10, borderRadius: 4 }, { backgroundColor: 'rgb(125, 76, 236)' }]}
                    labelStyle={{ fontSize: 12 }}
                    onPress={() => handleChildParkEntry(child.id, child.fullName)}
                  >
                    Oyun Parkı Girişiss
                  </Button>
                  */}
                </Card.Content>
              </Card>
            ))}
            
            
          </View>
        )}

        {/* Hızlı Erişim Kartları */}
        <View style={styles.sectionHeader}>
          <Title style={styles.sectionTitle}>Hızlı Erişim</Title>
        </View>

        <View style={styles.quickAccessContainer}>
          <Card style={styles.quickAccessCard} onPress={handleParkEntry}>
            <Card.Content style={styles.quickAccessContent}>
              <MaterialCommunityIcons name="gamepad-variant" size={40} color="rgb(125, 76, 236)" />
              <Text style={styles.quickAccessText}>Oyun Parkı Girişi</Text>
            </Card.Content>
          </Card>

          <Card style={styles.quickAccessCard} onPress={() => navigation.navigate('Menu')}>
            <Card.Content style={styles.quickAccessContent}>
              <MaterialCommunityIcons name="food" size={40} color="rgb(125, 76, 236)" />
              <Text style={styles.quickAccessText}>Menü</Text>
            </Card.Content>
          </Card>
        </View>
      </ScrollView>

      {/* Çocuk Oyun Parkı Girişi Onay Modalı */}
      <Portal>
        <Dialog 
          visible={confirmModalVisible} 
          onDismiss={() => setConfirmModalVisible(false)}
          style={{backgroundColor: 'rgba(255, 255, 255, 0.95)'}}
        >
          <Dialog.Title style={{color: 'rgb(125, 76, 236)'}}>Oyun Parkı Girişi</Dialog.Title>
          <Dialog.Content>
            <Text>{selectedChildName} olarak kayıtlı çocuğunuzun oyun alanına giriş talebini onaylıyor musunuz?
            </Text>
          </Dialog.Content>
          <Dialog.Actions>
            <Button onPress={() => setConfirmModalVisible(false)} textColor="rgb(125, 76, 236)">İptal</Button>
            <Button 
              onPress={() => createParkEntryForChild()}
              mode="contained"
              loading={isCreatingActivity}
              disabled={isCreatingActivity}
              buttonColor="rgb(125, 76, 236)"
              textColor="white"
            >
              Evet, Giriş Yap
            </Button>
          </Dialog.Actions>
        </Dialog>
      </Portal>

      {/* Uyarı Modalı */}
      <Portal>
        <Dialog 
          visible={warningModalVisible} 
          onDismiss={() => setWarningModalVisible(false)}
          style={{backgroundColor: 'rgba(255, 255, 255, 0.95)'}}
        >
          <Dialog.Title style={{color: 'rgb(125, 76, 236)'}}>Aktif Ziyaret Var</Dialog.Title>
          <Dialog.Content>
            <Text>{warningMessage}</Text>
          </Dialog.Content>
          <Dialog.Actions>
            <Button onPress={() => setWarningModalVisible(false)} textColor="rgb(125, 76, 236)">Tamam</Button>
            <Button 
              onPress={() => {
                setWarningModalVisible(false);
                navigation.navigate('Activities', { screen: 'ActiveParkActivities' });
              }}
              mode="contained"
              buttonColor="rgb(125, 76, 236)"
              textColor="white"
            >
              Aktif Ziyaretlere Git
            </Button>
          </Dialog.Actions>
        </Dialog>
      </Portal>
    </SafeAreaView>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: 'rgb(232, 162, 249)',
  },
  scrollContainer: {
    paddingVertical: 10,
  },
  activitiesContainer: {
    paddingHorizontal: 16,
  },
  activityCard: {
    marginBottom: 10,
    borderRadius: 8,
    elevation: 2,
    backgroundColor: 'rgba(255, 255, 255, 0.85)',
  },
  activityCardContent: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingVertical: 10,
  },
  activityIconContainer: {
    width: 40,
    height: 40,
    borderRadius: 20,
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: 'rgba(125, 76, 236, 0.2)',
  },
  activityTextContainer: {
    flex: 1,
    marginLeft: 10,
  },
  activityTitle: {
    fontSize: 16,
    fontWeight: '500',
  },
  activityCount: {
    fontSize: 14,
    color: '#666',
  },
  sectionHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingHorizontal: 16,
    marginBottom: 8,
  },
  sectionTitle: {
    fontSize: 18,
    fontWeight: 'bold',
    color: 'rgb(125, 76, 236)',
  },
  errorCard: {
    marginHorizontal: 16,
    marginBottom: 16,
    elevation: 2,
    borderRadius: 8,
    backgroundColor: 'rgba(255, 255, 255, 0.85)',
    borderColor: 'rgb(125, 76, 236)',
    borderWidth: 1,
  },
  errorText: {
    fontSize: 16,
    color: 'red',
    marginBottom: 10,
  },
  retryButton: {
    marginTop: 10,
  },
  loadingContainer: {
    justifyContent: 'center',
    alignItems: 'center',
    paddingVertical: 20,
  },
  loadingText: {
    fontSize: 16,
    color: '#666',
    marginTop: 10,
  },
  emptyCard: {
    marginHorizontal: 16,
    marginBottom: 16,
    elevation: 2,
    borderRadius: 8,
    backgroundColor: 'rgba(255, 255, 255, 0.85)',
  },
  emptyContent: {
    padding: 20,
    justifyContent: 'center',
    alignItems: 'center',
  },
  emptyText: {
    fontSize: 16,
    color: '#666',
    marginBottom: 10,
  },
  addButton: {
    marginTop: 10,
    backgroundColor: 'rgb(125, 76, 236)',
  },
  childrenContainer: {
    paddingHorizontal: 16,
  },
  childCard: {
    marginBottom: 10,
    borderRadius: 8,
    elevation: 2,
    backgroundColor: 'rgba(255, 255, 255, 0.85)',
  },
  childCardContent: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingVertical: 10,
  },
  childInfo: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
  },
  childAvatar: {
    width: 40,
    height: 40,
    borderRadius: 20,
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: 'rgb(125, 76, 236)',
  },
  childDetails: {
    marginLeft: 10,
  },
  childName: {
    fontSize: 16,
    fontWeight: '500',
    color: 'rgb(125, 76, 236)',
  },
  childAge: {
    fontSize: 14,
    color: '#666',
  },
  childParkButton: {
    marginLeft: 'auto',
    paddingHorizontal: 10,
    borderRadius: 4,
  },
  addChildButton: {
    marginTop: 10,
    flexDirection: 'row',
    alignItems: 'center',
  },
  addChildText: {
    fontSize: 16,
    color: '#666',
    marginLeft: 10,
  },
  quickAccessContainer: {
    flexDirection: 'column',
    paddingHorizontal: 16,
    marginTop: 10,
  },
  quickAccessCard: {
    marginBottom: 10,
    elevation: 2,
    borderRadius: 8,
    backgroundColor: 'rgba(255, 255, 255, 0.85)',
  },
  quickAccessContent: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: 15,
  },
  quickAccessText: {
    fontSize: 14,
    color: '#666',
    marginLeft: 10,
  },
});

export default HomeScreen;
